import { environment } from '../environments/environment';

/**
 * Log levels for controlling verbosity
 */
export enum LogLevel {
  NONE = 0,
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4,
  TRACE = 5
}

/**
 * Logger interface defining available logging methods
 */
export interface ILogger {
  trace(...args: any[]): void;
  debug(...args: any[]): void;
  log(...args: any[]): void;
  info(...args: any[]): void;
  warn(...args: any[]): void;
  error(...args: any[]): void;
  table(tabularData?: any, properties?: string[]): void;
}

/**
 * Configuration options for the logger
 */
interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
  useTimestamps: boolean;
  timestampFormat: 'iso' | 'locale' | 'time';
}

/**
 * Logger utility that controls console output based on environment settings.
 * Logs will only be shown in environments where enableLogs is true.
 * Logs will include the component/service name as a prefix in square brackets.
 */
class Logger {
  private config: LoggerConfig;

  constructor() {
    this.config = {
      level: LogLevel.INFO,
      enabled: environment.enableLogs || false,
      useTimestamps: true,
      timestampFormat: 'time'
    };
  }

  /**
   * Configure the logger settings
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Enable or disable logging
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Set the log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Format a timestamp based on the configured format
   */
  private formatTimestamp(): string {
    const now = new Date();
    
    switch (this.config.timestampFormat) {
      case 'iso':
        return now.toISOString();
      case 'locale':
        return now.toLocaleString();
      case 'time':
      default:
        return now.toLocaleTimeString();
    }
  }

  /**
   * Format a log message with source and optional timestamp
   */
  private formatMessage(source: string): string {
    if (this.config.useTimestamps) {
      return `[${this.formatTimestamp()}] [${source}]`;
    }
    return `[${source}]`;
  }

  /**
   * Creates a logger instance for a specific component or service
   * @param source Name of the component or service (will be shown in logs)
   */
  getLogger(source: string): ILogger {
    return {
      trace: (...args: any[]): void => {
        if (this.config.enabled && this.config.level >= LogLevel.TRACE) {
          console.debug(this.formatMessage(source), ...args);
        }
      },
      
      debug: (...args: any[]): void => {
        if (this.config.enabled && this.config.level >= LogLevel.DEBUG) {
          console.debug(this.formatMessage(source), ...args);
        }
      },
      
      log: (...args: any[]): void => {
        if (this.config.enabled && this.config.level >= LogLevel.INFO) {
          console.log(this.formatMessage(source), ...args);
        }
      },
      
      info: (...args: any[]): void => {
        if (this.config.enabled && this.config.level >= LogLevel.INFO) {
          console.info(this.formatMessage(source), ...args);
        }
      },
      
      warn: (...args: any[]): void => {
        if (this.config.enabled && this.config.level >= LogLevel.WARN) {
          console.warn(this.formatMessage(source), ...args);
        }
      },
      
      error: (...args: any[]): void => {
        // Errors are always logged unless level is NONE
        if (this.config.level >= LogLevel.ERROR) {
          console.error(this.formatMessage(source), ...args);
        }
      },
      
      table: (tabularData?: any, properties?: string[]): void => {
        if (this.config.enabled && this.config.level >= LogLevel.INFO) {
          console.log(this.formatMessage(source), 'Table:');
          console.table(tabularData, properties);
        }
      }
    };
  }
}

export const logger = new Logger();